/**
 * L3: the `w:` wallet-filter grammar (Quick Search design D-6 / D-8), ported from
 * native/quick-search/Sources/VowedKit/WalletToken.swift so the phone and the Mac read and write
 * the same text. One variable, two surfaces: the typed token and the scope pill are the same
 * state. `render` is the pill's direction (selection → text) and `parse` is the keyboard's
 * (text → selection); `parse(render(x)) == x` is what keeps them from diverging.
 *
 * `=` filter suggestions are the phone addition, from the 1Password iOS search screen
 * (01-references §2.1: typing `=` offers Vaults, Favorites and Categories).
 */
import { CATEGORIES, CATEGORY_LABEL, type Route } from "../contract.js";

export const WALLET_PREFIX = "w:";

export interface QueryParse {
  /** Wallets that resolved against the live vault, in vault order. */
  wallets: string[];
  /** Named in the token but not a real wallet: surfaced, never silently empty. */
  unknown: string[];
  /** The search term, with the token stripped. Never contains `w:`. */
  query: string;
  /** The in-progress fragment while the token is still being typed; null once committed. */
  completing: string | null;
}

/**
 * Selection → text. No wallets or every wallet writes no token at all (no orphan prefix; all
 * wallets is not a filter). Otherwise `w:a,b ` in vault order, never click order.
 */
export function renderQuery(wallets: Iterable<string>, query: string, order: string[]): string {
  const set = new Set(wallets);
  const selected = order.filter((w) => set.has(w));
  if (selected.length === 0 || selected.length === order.length) return query;
  return `${WALLET_PREFIX}${selected.join(",")} ${query}`;
}

/** Text → selection. `known` is the live wallet list, in vault order. */
export function parseQuery(text: string, known: string[]): QueryParse {
  if (!text.startsWith(WALLET_PREFIX)) return { wallets: [], unknown: [], query: text, completing: null };
  const after = text.slice(WALLET_PREFIX.length);
  const space = after.indexOf(" ");
  // A space ends the token. No space means the operator is still typing it.
  if (space < 0) {
    const s = split(after, known, true);
    return { wallets: s.wallets, unknown: s.unknown, query: "", completing: s.fragment };
  }
  const s = split(after.slice(0, space), known, false);
  return { wallets: s.wallets, unknown: s.unknown, query: after.slice(space + 1), completing: null };
}

function split(list: string, known: string[], completing: boolean) {
  if (list === "") return { wallets: [] as string[], unknown: [] as string[], fragment: completing ? "" : null };
  const parts = list.split(",");
  let fragment: string | null = null;
  // While typing, the LAST part is still in flight: `w:coi,v` means coi is chosen and "v" is
  // being completed, not that a wallet named "v" is missing.
  if (completing && !list.endsWith(",")) fragment = parts.pop() ?? "";
  const chosen = new Set<string>();
  const unknown: string[] = [];
  for (const p of parts) {
    if (!p) continue;
    const hit = known.find((k) => k.toLowerCase() === p.toLowerCase());
    if (hit) chosen.add(hit);
    else unknown.push(p);
  }
  return { wallets: known.filter((k) => chosen.has(k)), unknown, fragment: fragment ?? (completing ? "" : null) };
}

/** Wallets offered for the in-flight fragment: matching ones, minus any already chosen. */
export function walletCompletions(fragment: string, known: string[], chosen: string[]): string[] {
  const f = fragment.toLowerCase();
  return known.filter((w) => !chosen.includes(w) && (f === "" || w.toLowerCase().includes(f)));
}

/**
 * The pill's tick/untick: toggle one wallet and rewrite the field. The query survives
 * untouched; only real wallets are written (it writes what you could type).
 */
export function toggleWalletInText(text: string, wallet: string, order: string[]): string {
  const p = parseQuery(text, order);
  const sel = new Set(p.wallets);
  if (sel.has(wallet)) sel.delete(wallet);
  else sel.add(wallet);
  return renderQuery(sel, p.query, order);
}

/**
 * The caret never moves relative to the query: the rewrite happens to the left of it, so the
 * caret keeps its distance from the end of the field.
 */
export function caretAfterRewrite(oldText: string, newText: string, oldCaret: number): number {
  const fromEnd = oldText.length - oldCaret;
  return Math.max(0, Math.min(newText.length, newText.length - fromEnd));
}

export type FilterSuggestion =
  | { kind: "wallet-token"; label: string }
  | { kind: "list"; list: NonNullable<Route["list"]>; label: string };

/** `=` opens the filter menu; the text after `=` narrows it. Null when not in filter mode. */
export function filterSuggestions(text: string): FilterSuggestion[] | null {
  if (!text.startsWith("=")) return null;
  const f = text.slice(1).trim().toLowerCase();
  const categories = [...CATEGORIES]
    .sort((a, b) => CATEGORY_LABEL[a].localeCompare(CATEGORY_LABEL[b]))
    .map((c): FilterSuggestion => ({ kind: "list", list: c, label: CATEGORY_LABEL[c] }));
  const all: FilterSuggestion[] = [
    { kind: "wallet-token", label: "Wallet" },
    { kind: "list", list: "favorites", label: "Favorites" },
    ...categories,
  ];
  return all.filter((s) => f === "" || s.label.toLowerCase().includes(f));
}
